import { Component, computed, input } from '@angular/core';
import { statusLabel, StatusSeverity, statusSeverity } from '../../../core/status';

export type StatusTone = 'success' | 'info' | 'warn' | 'danger' | 'neutral';

const TONE_BY_SEVERITY: Readonly<Record<StatusSeverity, StatusTone>> = {
    success: 'success',
    info: 'info',
    warn: 'warn',
    danger: 'danger',
    secondary: 'neutral',
    contrast: 'neutral'
};

/**
 * A status as colour *and* word. Colour alone would leave the meaning to
 * whoever can tell the tints apart; the label is what the row actually reads.
 *
 * An API status goes in `status`, and core/status.ts gives it its word and tone.
 * A state that is not one of those, such as whether a product is visible, sets
 * its own `tone` and writes the word as content:
 * `<app-status-tag tone="success">Sim</app-status-tag>`.
 */
@Component({
    selector: 'app-status-tag',
    template: `
        <span class="status-tag" [attr.data-tone]="appliedTone()">
            <span class="status-tag__dot" aria-hidden="true"></span>
            {{ label() }}<ng-content />
        </span>
    `,
    styles: `
        .status-tag {
            display: inline-flex;
            align-items: center;
            gap: 0.375rem;
            padding: 0.25rem 0.625rem;
            border-radius: 999px;
            background: var(--status-tag-surface);
            color: var(--status-tag-color);
            font-size: 0.75rem;
            font-weight: 600;
            line-height: 1.25;
            white-space: nowrap;
        }

        .status-tag__dot {
            width: 0.375rem;
            height: 0.375rem;
            border-radius: 50%;
            background: currentColor;
            flex: none;
        }

        .status-tag[data-tone='success'] {
            --status-tag-color: var(--app-state-success-color);
            --status-tag-surface: var(--app-state-success-surface);
        }

        .status-tag[data-tone='warn'] {
            --status-tag-color: var(--app-state-warn-color);
            --status-tag-surface: var(--app-state-warn-surface);
        }

        .status-tag[data-tone='danger'] {
            --status-tag-color: var(--app-state-danger-color);
            --status-tag-surface: var(--app-state-danger-surface);
        }

        .status-tag[data-tone='info'] {
            --status-tag-color: var(--app-state-info-color);
            --status-tag-surface: var(--app-state-info-surface);
        }

        .status-tag[data-tone='neutral'] {
            --status-tag-color: var(--app-state-neutral-color);
            --status-tag-surface: var(--app-state-neutral-surface);
        }
    `
})
export class StatusTagComponent {
    readonly status = input('');
    readonly tone = input<StatusTone>();

    protected readonly label = computed(() => (this.status() ? statusLabel(this.status()) : ''));
    protected readonly appliedTone = computed(() => this.tone() ?? TONE_BY_SEVERITY[statusSeverity(this.status())]);
}
